/** Shared column and cell transformations used by CSV exports and log previews. */
import { LogsApiSystemColumns } from '../TypeScript/enums';

/**
 * Properties that are not relevant for CSV export
 */
export const CSV_EXCLUDED_COLUMNS = [
  'begin_x',
  'begin_y',
  'begin_z',
  'end_x',
  'end_y',
  'end_z',
  'mid_x',
  'mid_y',
  'mid_z',
  'model_train_id',
  'model_train_box_id',
  'image_id',
  'script_version',
  'created_by',
  'created_at',
  'updated_by',
  'updated_at',
  'image_name',
  'original_ext',
  'report_id',
  'report_cluster_count',
  'split_ext',
];

/**
 * Column name mappings for CSV export (snake_case -> user-friendly names)
 */
export const CSV_COLUMN_RENAME_MAP: Record<string, string> = {
  drillhole_id: 'drillhole_name',
  drillhole: 'drillhole_name',
  drill_hole_name: 'drillhole_name',
  depth_from: 'from',
  depth_to: 'to',
  midpoint_m: 'midpoint',
  cluster_id: 'domain_id',
  cluster_name: 'domain_name',
};

export const DEPTH_COLUMNS = ['depth_to', 'depth_from', 'midpoint_m'];

/**
 * Priority column order for CSV export
 */
export const CSV_PRIORITY_COLUMNS = [
  'drillhole_id',
  'drillhole',
  'drill_hole_name',
  'depth_from',
  'depth_to',
  'midpoint_m',
  'log_id',
  'image_type',
  'calculation_type',
  'first_dom',
  'second_dom',
  'third_dom',
  'average_color',
];

export const shouldSkipColumnNormalization = (logType?: string): boolean =>
  logType === 'custom';

/**
 * Sort columns by priority, then alphabetically
 * @description
 * Columns in the `CSV_PRIORITY_COLUMNS` array will appear first in the specified order,
 * followed by any remaining columns sorted alphabetically (with letter-starting columns first).
 * @param {string[]} columns - The column names to sort.
 * @return {string[]} The sorted column names.
 */
export const sortColumnsByPriority = (columns: string[]): string[] => {
  const priorityFound = CSV_PRIORITY_COLUMNS.filter((column) =>
    columns.includes(column),
  );
  const remaining = columns
    .filter((column) => !CSV_PRIORITY_COLUMNS.includes(column))
    .sort((a, b) => {
      const aStartsWithLetter = /^[a-zA-Z]/.test(a);
      const bStartsWithLetter = /^[a-zA-Z]/.test(b);

      if (aStartsWithLetter && bStartsWithLetter) return a.localeCompare(b);
      if (aStartsWithLetter) return -1;
      if (bStartsWithLetter) return 1;
      return a.localeCompare(b);
    });

  return [...priorityFound, ...remaining];
};

/**
 * Ensure a column name is in snake_case
 * @param {string} str - The column name to convert.
 * Example: fractureFrequency -> fracture_frequency
 *          fracture_frequency -> fracture_frequency
 */
export const toSnakeCase = (value: string): string => {
  return value
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .split('_')
    .map((word) => word.toLowerCase())
    .join('_');
};

export const isExcludedExportColumn = (columnName: string): boolean => {
  return columnName === '_id' || CSV_EXCLUDED_COLUMNS.includes(columnName);
};

/**
 * Round numeric values to 3 decimal points if it's in a depth column
 */
export const roundDepthValue = (columnName: string, value: any): any => {
  const PRECISION = 3;
  if (DEPTH_COLUMNS.includes(columnName) && typeof value === 'number') {
    return parseFloat(value.toFixed(PRECISION));
  }

  return value;
};

/**
 * Helper function to round off column values to 3 decimal places
 * @description
 * It identifies the original column names in the data row that correspond to the provided column names
 * and rounds off the values in those columns to 3 decimal places if they are numbers.
 * @param {Record<string, any>} rowItem - The data row to modify.
 * @param {string[]} columnNames - The column names whose values need to be rounded off.
 * @return {Record<string, any>} The modified data row with rounded values for the specified columns.
 */
export const roundOffColumnValues = (
  rowItem: Record<string, any>,
  columnNames: string[] = DEPTH_COLUMNS,
): Record<string, any> => {
  const result = { ...rowItem };
  columnNames.forEach((columnName) => {
    if (columnName in result) {
      result[columnName] = roundDepthValue(columnName, result[columnName]);
    }
  });
  return result;
};

/**
 * Transform column name for CSV header (apply renaming and handle special patterns)
 * @param {string} column - The column name to transform.
 * @return {string} The transformed column name.
 */
export const transformColumnName = (column: string): string => {
  // Apply direct mapping if exists
  if (CSV_COLUMN_RENAME_MAP[column]) {
    return CSV_COLUMN_RENAME_MAP[column];
  }

  // Handle dynamic cutoff columns with number suffixes
  // e.g., cluster_name_cutoff_15 → domain_name_15
  const cutoffMatch = column.match(
    /^(cluster_name_cutoff|cluster_color_cutoff|cluster_id_cutoff)[_-](\d+)$/,
  );
  if (cutoffMatch) {
    const [, fieldName, number] = cutoffMatch;
    const cutoffMappings: Record<string, string> = {
      cluster_name_cutoff: 'domain_name',
      cluster_color_cutoff: 'domain_color',
      cluster_id_cutoff: 'domain_id',
    };
    return `${cutoffMappings[fieldName]}_${number}`;
  }

  return column;
};

export const resolveDrillholeName = (
  value: any,
  drillholesMapById: Record<string | number, { name: string }>,
): any => {
  return drillholesMapById?.[value]?.name || value;
};

/**
 * Replaces drillhole IDs with names
 *
 * @param {T} item - The data row to transform.
 * @param {any} drillholesMapById - The map of drillholes by ID.
 * @return {Record<string, any>} The transformed data row.
 */
export const replaceDrillholeIdWithName = <T extends Record<string, any>>(
  item: T,
  drillholesMapById: Record<string | number, { name: string }>,
): T => {
  const itemCopy = { ...item } as Record<string, any>;
  const drillholeIdField =
    'drillholeId' in itemCopy ? 'drillholeId' : 'drillhole_id';

  if (drillholeIdField in itemCopy) {
    itemCopy[drillholeIdField] = resolveDrillholeName(
      itemCopy[drillholeIdField],
      drillholesMapById,
    );
  }

  return itemCopy as T;
};

/**
 * Use shared transformations to format log data the way it would appear in
 * the downloaded csv version.
 */
export const transformDataframe = (
  dataframe: Record<string, Record<string | number, any>>,
  drillholesMapById: Record<string | number, { name: string }>,
  skipColumnNormalization = false,
): Record<string, Record<string, any>> => {
  const transformed: Record<string, Record<string, any>> = {};

  Object.entries(dataframe).forEach(([originalColumn, values]) => {
    const columnName = skipColumnNormalization
      ? originalColumn
      : toSnakeCase(originalColumn);
    if (isExcludedExportColumn(columnName)) {
      return;
    }

    transformed[columnName] = Object.entries(values || {}).reduce(
      (columnValues, [rowIndex, value]) => {
        const resolvedValue =
          columnName === LogsApiSystemColumns.drillholeId
            ? resolveDrillholeName(value, drillholesMapById)
            : value;
        columnValues[rowIndex] = roundDepthValue(columnName, resolvedValue);
        return columnValues;
      },
      {} as Record<string, any>,
    );
  });

  return transformed;
};
